/**
 * Inventory Forecasting Tool - Main Page Component
 *
 * This is the entry point for the Inventory Forecasting feature. It provides
 * a comprehensive view of inventory levels, projected sales, and incoming POs
 * across different time periods and geographies.
 *
 * Architecture Overview:
 * ---------------------
 * - State Management: All state lives in this component and is passed down to children
 * - Data Flow: API → State → Derived Data (useMemo) → UI Components
 * - Components: Modular components in ./components/ handle rendering
 * - Types: Shared types in ./types.ts
 * - Utils: Pure utility functions in ./utils.ts
 *
 * Key Features:
 * -------------
 * 1. Inventory Display: Shows inventory grouped by inventory_id + country
 * 2. Time Period Aggregation: Day/Week/Month/Quarter buckets
 * 3. Projections: Chain from run rate projections
 * 4. Sales Modifiers: Global and per-inventory multipliers for projections
 * 5. Purchase Orders: Manage incoming POs that affect inventory projections
 * 6. Filtering: Search by inventory name/ID/SKU, filter by country/manufacturer
 *
 * API Integration:
 * ----------------
 * - GET /run-rate: Sales run rate data and modifiers
 * - GET /forecasts: Server-computed groupings, ledgers and risk (drives the table)
 * - GET /inventory-history: Historical inventory data (lazy loaded per page)
 * - PUT /incoming-pos: Save purchase orders
 * - DELETE /incoming-pos: Delete purchase orders
 * - PUT /run-rate-modifiers: Save sales modifiers
 *
 * Performance Considerations:
 * ---------------------------
 * - Lazy loading of inventory history (only fetches visible page)
 * - Caching of inventory history by date range + inventory ID
 * - Debounced API calls (FETCH_SETTLE_MS) to prevent excessive requests
 * - Memoized derived data (allInventoryGroupings, periodColumns, metricValues)
 *
 * @module Inventory
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import InventoryService, { CatalogSuggestions, LeadTimesResponse, PackSubstitutions } from "services/InventoryService";
import SavePricesToSkuMasterDialog, { PriceChange, ProductNumberChange, findPriceChanges, findProductNumberChanges } from "shared/components/dialogs/SavePricesToSkuMasterDialog";
import { downloadBlob } from "shared/utils/downloadBlob";
import { persistPO } from "shared/utils/persistPO";
import CashflowService from "services/CashflowService";
import { Supplier } from "models/CashflowModel";
import { usePOManagement, POSaveParams } from "shared/hooks/usePOManagement";
import {
  derivePOsByInventory,
  InventoryGrouping,
  MonthlySalesModifiers,
  IncomingPO,
  DailyRunRateModifiers,
  GlobalRunRateModifier,
  InventoryRunRateModifier,
  PurchaseOrder,
  WRO,
  WroLinkRequest,
  WroAllocationsRequest, WroLineAllocationsRequest,
} from "models/InventoryModel";

import {
  BucketGranularity,
  SalesModifiersData,
  RunRateBasisMode,
  SkuModalData,
  PeriodColumn,
  PeriodMetrics,
  MonthlyModifiers,
  InventoryModifiers,
  ManualOverrides,
  RiskTimeframe,
  InventoryRiskItem,
} from "./types";

import {
  ControlPanel,
  InventoryTable,
  SkuListModal,
  DeletePOConfirmation,
  POManagementDialog,
  SalesProjectionDetailDialog,
  RiskSummarySection,
  LeadTimesDialog,
} from "./components";
import InventoryDashboard from "./components/InventoryDashboard";
import {
  PRODUCT_SORT, ProductSort, ProductArrangement, MovePosition, DEFAULT_ARRANGEMENT,
  loadProductArrangement, saveProductArrangement, isDefaultArrangement, sortGroupings, moveKey,
} from "./productSort";
import { FamilyControls, ProductNeighbors, ProductRowControls, WhatIfControls } from "./components/InventoryTable";
import { DemandSettingsByInventory, EMPTY_DEMAND_SETTINGS, applyForecastUpdates, productDemandSettings } from "./demandWhatIf";
import { useDemandWhatIf } from "./useDemandWhatIf";
import SaveWhatIfDialog from "./components/dialogs/SaveWhatIfDialog";

// Wait for settings to stop changing before hitting the API: the saved view is restored right
// after mount, and the risk/buffer steppers are clicked in bursts.
const FETCH_SETTLE_MS = 250;

// Family rows come from the server keyed family:<single item's inventory id>||<country>
const FAMILY_INVENTORY_ID_PREFIX = "family:";
const COLLAPSED_FAMILIES_KEY = "inventory-forecasting:collapsed-families";
const FAMILY_VIEW_KEY = "inventory-forecasting:family-view";
const COLLAPSED_ROWS_KEY = "inventory-forecasting:collapsed-rows";
/**
 * Whether the projections table shows each family's consolidated "all packs" row. The SKUs in a
 * pack are never hidden (Inno, 2026-09-24): without the consolidated row each one is simply an
 * ordinary product row.
 */
const FAMILY_VIEW = {
  /** Family blocks: the consolidated "all packs" row, then its SKUs */
  SHOW_CONSOLIDATED: "show_consolidated",
  /** No consolidated rows: each SKU in a pack is an ordinary product row */
  HIDE_CONSOLIDATED: "hide_consolidated",
} as const;
type FamilyView = typeof FAMILY_VIEW[keyof typeof FAMILY_VIEW];
const isFamilyView = (value: unknown): value is FamilyView => Object.values(FAMILY_VIEW).includes(value as FamilyView);
// How far back the "orders that shipped a different pack than usual" count looks
const PACK_SUBSTITUTION_DAYS = 30;

/** Base units in one unit of a family member (1 for the single item) */
function familyMemberQty(member: InventoryGrouping, familyKeyByMemberKey: Map<string, string>, groupings: InventoryGrouping[]): number {
  const familyKey = familyKeyByMemberKey.get(member.key);
  const family = groupings.find(grouping => grouping.key === familyKey)?.family;
  return family?.members.find(entry => entry.key === member.key)?.qty ?? 1;
}

// How many changes to the view (sort, pins, order) Undo can walk back through
const VIEW_UNDO_LIMIT = 50;
type ViewSnapshot = { arrangement: ProductArrangement; pageIndex: number };

// ============================================================================
// Main Component
// ============================================================================

export default function InventoryPage(): JSX.Element {
  const inventoryService = useMemo(() => new InventoryService(), []);
  const cashflowService = useMemo(() => new CashflowService(), []);

  // -------------------------------------------------------------------------
  // Display & UI State
  // -------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<"forecasting" | "summary">("forecasting");
  const [bucketGranularity, setBucketGranularity] = useState<BucketGranularity>("month");
  const [groupingsPerPage, setGroupingsPerPage] = useState<number>(5);
  // Sort, pinned products and hand-made order: one person's view, kept in this browser
  const [productArrangement, setProductArrangement] = useState<ProductArrangement>(loadProductArrangement);
  // What the view looked like before each change since the page opened, newest last (for Undo)
  const [viewUndoStack, setViewUndoStack] = useState<ViewSnapshot[]>([]);

  // -------------------------------------------------------------------------
  // Loading & Error State
  // -------------------------------------------------------------------------
  const [isLoadingForecasts, setIsLoadingForecasts] = useState<boolean>(true);
  const [forecastsPayload, setForecastsPayload] = useState<any | null>(null);
  // The table waits on /forecasts alone; the page's other reads (lead times, suppliers, prices,
  // pack swaps, POs, WROs) start once its first request has finished — answered or failed — so
  // they do not compete with it for the API's Snowflake connection (a few queries at a time).
  const [hasForecast, setHasForecast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  // Bumped by a PO save / delete: re-runs the forecast and the PO lists like refreshTrigger, but
  // does not re-sync the modifier editors, so unsaved modifier edits survive a PO save
  const [poSaveVersion, setPoSaveVersion] = useState<number>(0);

  // Combined loading state for UI
  const isLoading = isLoadingForecasts;

  // -------------------------------------------------------------------------
  // OOS Alert Ignores
  // -------------------------------------------------------------------------
  const [ignoredAlertKeys, setIgnoredAlertKeys] = useState<Set<string>>(new Set());
  const [ignoringKey, setIgnoringKey] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // API Response Payloads
  // -------------------------------------------------------------------------
  const [runRatePayload, setRunRatePayload] = useState<any | null>(null);
  // SKU master prices + Danican product numbers (PO line pre-fill; the table's product # and sort)
  const [catalogSuggestions, setCatalogSuggestions] = useState<CatalogSuggestions | null>(null);

  // -------------------------------------------------------------------------
  // Filter State (Applied = used by filteredInventoryGroupings, Staged = user edits)
  // -------------------------------------------------------------------------
  const [skuModalData, setSkuModalData] = useState<SkuModalData>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  // Applied state — these drive the filtered table
  const [appliedInventoryFilterText, setAppliedInventoryFilterText] = useState<string>("");
  const [appliedSelectedCountries, setAppliedSelectedCountries] = useState<string[]>([]);
  const [appliedSelectedManufacturers, setAppliedSelectedManufacturers] = useState<string[]>([]);
  const [appliedSelectedBrands, setAppliedSelectedBrands] = useState<string[]>([]);
  const [appliedHasUserInteractedWithManufacturerFilter, setAppliedHasUserInteractedWithManufacturerFilter] = useState(false);
  const [appliedHasUserInteractedWithCountryFilter, setAppliedHasUserInteractedWithCountryFilter] = useState(false);
  const [appliedHasUserInteractedWithBrandFilter, setAppliedHasUserInteractedWithBrandFilter] = useState(false);

  // Staged state — ControlPanel edits these, applied on Refresh click
  const [stagedInventoryFilterText, setStagedInventoryFilterText] = useState<string>("");
  const [stagedSelectedCountries, setStagedSelectedCountries] = useState<string[]>([]);
  const [stagedSelectedManufacturers, setStagedSelectedManufacturers] = useState<string[]>([]);
  const [stagedSelectedBrands, setStagedSelectedBrands] = useState<string[]>([]);
  const [stagedHasUserInteractedWithManufacturerFilter, setStagedHasUserInteractedWithManufacturerFilter] = useState(false);
  const [stagedHasUserInteractedWithCountryFilter, setStagedHasUserInteractedWithCountryFilter] = useState(false);
  const [stagedHasUserInteractedWithBrandFilter, setStagedHasUserInteractedWithBrandFilter] = useState(false);

  // -------------------------------------------------------------------------
  // Date & Run Rate State (Applied = triggers API, Staged = user edits)
  // -------------------------------------------------------------------------
  const defaultStartDate = moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD");
  const defaultEndDate = moment().add(3, "months").endOf("month").format("YYYY-MM-DD");

  // Applied state — these trigger API calls
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [runRateBasis, setRunRateBasis] = useState<number>(7);
  const [runRateBasisMode, setRunRateBasisMode] = useState<RunRateBasisMode>("rolling");
  const [fixedRunRateStart, setFixedRunRateStart] = useState<string | null>(null);
  const [fixedRunRateEnd, setFixedRunRateEnd] = useState<string | null>(null);

  // Staged state — user edits these, applied on "Apply Settings" click
  const [stagedStartDate, setStagedStartDate] = useState<string>(defaultStartDate);
  const [stagedEndDate, setStagedEndDate] = useState<string>(defaultEndDate);
  const [stagedRunRateBasisInput, setStagedRunRateBasisInput] = useState<string>("7");
  const [stagedRunRateBasisMode, setStagedRunRateBasisMode] = useState<RunRateBasisMode>("rolling");
  const [stagedFixedRunRateStart, setStagedFixedRunRateStart] = useState<string | null>(null);
  const [stagedFixedRunRateEnd, setStagedFixedRunRateEnd] = useState<string | null>(null);

  // Dirty indicator: staged differs from applied
  const isSettingsDirty =
    stagedStartDate !== startDate ||
    stagedEndDate !== endDate ||
    stagedRunRateBasisMode !== runRateBasisMode ||
    (stagedRunRateBasisMode === "rolling" && parseInt(stagedRunRateBasisInput, 10) !== runRateBasis) ||
    stagedFixedRunRateStart !== fixedRunRateStart ||
    stagedFixedRunRateEnd !== fixedRunRateEnd ||
    stagedInventoryFilterText !== appliedInventoryFilterText ||
    JSON.stringify(stagedSelectedCountries) !== JSON.stringify(appliedSelectedCountries) ||
    JSON.stringify(stagedSelectedManufacturers) !== JSON.stringify(appliedSelectedManufacturers) ||
    JSON.stringify(stagedSelectedBrands) !== JSON.stringify(appliedSelectedBrands) ||
    stagedHasUserInteractedWithManufacturerFilter !== appliedHasUserInteractedWithManufacturerFilter ||
    stagedHasUserInteractedWithCountryFilter !== appliedHasUserInteractedWithCountryFilter ||
    stagedHasUserInteractedWithBrandFilter !== appliedHasUserInteractedWithBrandFilter;

  // Apply any staged settings and force re-fetch
  const forceFreshForecastRef = useRef(false);
  const handleRefresh = () => {
    setStartDate(stagedStartDate);
    setEndDate(stagedEndDate);
    setRunRateBasisMode(stagedRunRateBasisMode);
    setFixedRunRateStart(stagedFixedRunRateStart);
    setFixedRunRateEnd(stagedFixedRunRateEnd);
    const value = parseInt(stagedRunRateBasisInput, 10);
    if (!isNaN(value) && value >= 1 && value <= 90) {
      setRunRateBasis(value);
    }
    // Apply staged filters
    setAppliedInventoryFilterText(stagedInventoryFilterText);
    setAppliedSelectedCountries(stagedSelectedCountries);
    setAppliedSelectedManufacturers(stagedSelectedManufacturers);
    setAppliedSelectedBrands(stagedSelectedBrands);
    setAppliedHasUserInteractedWithManufacturerFilter(stagedHasUserInteractedWithManufacturerFilter);
    setAppliedHasUserInteractedWithCountryFilter(stagedHasUserInteractedWithCountryFilter);
    setAppliedHasUserInteractedWithBrandFilter(stagedHasUserInteractedWithBrandFilter);
    // Re-read when the date / run-rate settings changed (filters alone apply instantly, as before).
    // Refresh with nothing changed re-reads past the server's ten-minute forecast cache: it is how
    // to see a ShipBob sync, or any change made outside this page, straight away.
    const apiSettingsChanged =
      stagedStartDate !== startDate ||
      stagedEndDate !== endDate ||
      stagedRunRateBasisMode !== runRateBasisMode ||
      stagedFixedRunRateStart !== fixedRunRateStart ||
      stagedFixedRunRateEnd !== fixedRunRateEnd ||
      (stagedRunRateBasisMode === "rolling" && parseInt(stagedRunRateBasisInput, 10) !== runRateBasis);
    if (apiSettingsChanged) {
      setRefreshTrigger((prev) => prev + 1);
    } else if (!isSettingsDirty) {
      forceFreshForecastRef.current = true;
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  // -------------------------------------------------------------------------
  // Sales Modifiers State
  // -------------------------------------------------------------------------
  const [salesModifiers, setSalesModifiers] = useState<SalesModifiersData>({ global: {}, byInventory: {} });
  const [editingInventoryModifiers, setEditingInventoryModifiers] = useState<string | null>(null);
  const [isSavingModifiers, setIsSavingModifiers] = useState<boolean>(false);

  // -------------------------------------------------------------------------
  // Consolidated PO Management State
  // -------------------------------------------------------------------------
  const [consolidatedPOs, setConsolidatedPOs] = useState<PurchaseOrder[]>([]);
  const [wros, setWros] = useState<WRO[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // -------------------------------------------------------------------------
  // Sales Projection Detail Dialog State
  // -------------------------------------------------------------------------
  const [projectionDetailInventoryGrouping, setProjectionDetailInventoryGrouping] = useState<InventoryGrouping | null>(null);
  const [manualOverrides, setManualOverrides] = useState<ManualOverrides>({});
  // Each product's adjustments as the server has them; what differs on screen is an unsaved what-if
  const [savedDemandSettings, setSavedDemandSettings] = useState<DemandSettingsByInventory>(EMPTY_DEMAND_SETTINGS);
  // The product whose what-if is waiting on the "visible to everyone" confirmation
  const [savingWhatIfInventoryId, setSavingWhatIfInventoryId] = useState<string | null>(null);
  const [isConfirmingSaveSettings, setIsConfirmingSaveSettings] = useState<boolean>(false);

  // -------------------------------------------------------------------------
  // Risk Summary State
  // -------------------------------------------------------------------------
  const [riskTimeframe, setRiskTimeframe] = useState<RiskTimeframe>(3.5);
  const [bufferMonths, setBufferMonths] = useState<number>(1);
  const [leadTimes, setLeadTimes] = useState<LeadTimesResponse | null>(null);
  const [isLeadTimesDialogOpen, setIsLeadTimesDialogOpen] = useState<boolean>(false);

  // Reference for scrolling to table rows
  const tableRowRefs = useRef<Map<string, HTMLElement>>(new Map());

  // =========================================================================
  // SESSION STORAGE PERSISTENCE
  // =========================================================================

  const SESSION_STORAGE_KEY = "inventory-forecasting-state";

  // Restore state from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.inventoryFilterText) {
          setStagedInventoryFilterText(parsed.inventoryFilterText);
          setAppliedInventoryFilterText(parsed.inventoryFilterText);
        }
        if (parsed.startDate) {
          setStagedStartDate(parsed.startDate);
          setStartDate(parsed.startDate);
        }
        if (parsed.endDate) {
          setStagedEndDate(parsed.endDate);
          setEndDate(parsed.endDate);
        }
        if (parsed.bucketGranularity) setBucketGranularity(parsed.bucketGranularity);
        if (parsed.groupingsPerPage) setGroupingsPerPage(parsed.groupingsPerPage);
        if (parsed.currentPageIndex != null) setCurrentPageIndex(parsed.currentPageIndex);
        if (parsed.runRateBasisInput) setStagedRunRateBasisInput(parsed.runRateBasisInput);
        if (parsed.runRateBasisMode) {
          setStagedRunRateBasisMode(parsed.runRateBasisMode);
          setRunRateBasisMode(parsed.runRateBasisMode);
        }
      }
    } catch {
      // Ignore parse errors
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filter state on changes
  useEffect(() => {
    const state = {
      inventoryFilterText: stagedInventoryFilterText,
      startDate: stagedStartDate,
      endDate: stagedEndDate,
      bucketGranularity,
      groupingsPerPage,
      currentPageIndex,
      runRateBasisInput: stagedRunRateBasisInput,
      runRateBasisMode: stagedRunRateBasisMode,
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  }, [stagedInventoryFilterText, stagedStartDate, stagedEndDate, bucketGranularity, groupingsPerPage, currentPageIndex, stagedRunRateBasisInput, stagedRunRateBasisMode]);

  // =========================================================================
  // SALES MODIFIERS LOGIC
  // =========================================================================

  /**
   * Sync sales modifiers + manual overrides from API whenever a new runRatePayload
   * reference arrives. Keyed on payload identity, not a boolean flag, so toggling
   * a flag doesn't cause us to re-merge the same stale payload and lock out the
   * next real refetch.
   */
  const lastMergedRunRatePayloadRef = useRef<any>(null);
  useEffect(() => {
    if (!runRatePayload?.runRateModifiers) return;
    if (runRatePayload === lastMergedRunRatePayloadRef.current) return;
    lastMergedRunRatePayloadRef.current = runRatePayload;

    const apiModifiers = runRatePayload.runRateModifiers;

    const globalByMonth: MonthlyModifiers = {};
    for (const mod of apiModifiers.globalModifiers || []) {
      if (mod.date && mod.modifier !== undefined) {
        const monthKey = moment(mod.date).format("YYYY-MM");
        globalByMonth[monthKey] = mod.modifier;
      }
    }

    const byInventory: InventoryModifiers = {};
    for (const mod of apiModifiers.inventoryModifiers || []) {
      if (mod.inventoryId && mod.date && mod.modifier !== undefined) {
        const monthKey = moment(mod.date).format("YYYY-MM");
        if (!byInventory[mod.inventoryId]) {
          byInventory[mod.inventoryId] = {};
        }
        byInventory[mod.inventoryId][monthKey] = mod.modifier;
      }
    }

    setSalesModifiers({ global: globalByMonth, byInventory });

    const loadedManualOverrides: ManualOverrides = {};
    for (const override of apiModifiers.manualOverrides || []) {
      if (override.inventoryId && override.periodKey && override.value !== undefined) {
        if (!loadedManualOverrides[override.inventoryId]) {
          loadedManualOverrides[override.inventoryId] = {};
        }
        loadedManualOverrides[override.inventoryId][override.periodKey] = override.value;
      }
    }
    setManualOverrides(loadedManualOverrides);
    setSavedDemandSettings({ byInventory, manualOverrides: loadedManualOverrides });
  }, [runRatePayload]);

  const updateGlobalModifier = (monthKey: string, value: number | null) => {
    setSalesModifiers((prev) => {
      const newGlobal = { ...prev.global };
      if (value === null) {
        delete newGlobal[monthKey];
      } else {
        newGlobal[monthKey] = value;
      }
      return { ...prev, global: newGlobal };
    });
  };

  const updateInventoryModifier = (inventoryId: string, monthKey: string, value: number | null) => {
    setSalesModifiers((prev) => {
      const newByInventory = { ...prev.byInventory };
      if (!newByInventory[inventoryId]) {
        newByInventory[inventoryId] = {};
      }
      const inventoryModifiers = { ...newByInventory[inventoryId] };
      if (value === null) {
        delete inventoryModifiers[monthKey];
      } else {
        inventoryModifiers[monthKey] = value;
      }
      if (Object.keys(inventoryModifiers).length === 0) {
        delete newByInventory[inventoryId];
      } else {
        newByInventory[inventoryId] = inventoryModifiers;
      }
      return { ...prev, byInventory: newByInventory };
    });
  };

  const clearInventoryModifiers = (inventoryId: string) => {
    setSalesModifiers((prev) => {
      const newByInventory = { ...prev.byInventory };
      delete newByInventory[inventoryId];
      return { ...prev, byInventory: newByInventory };
    });
  };

  const saveSettingsToApi = async () => {
    setIsSavingModifiers(true);
    try {
      // Collect all inventory IDs that have modifiers or overrides
      const inventoryIds = new Set<string>([
        ...Object.keys(salesModifiers.byInventory),
        ...Object.keys(manualOverrides),
      ]);

      // Build global modifiers array (sent with each inventory request)
      const globalModifiers: Array<{ date: string; modifier: number }> = [];
      for (const [monthKey, modifier] of Object.entries(salesModifiers.global)) {
        const date = moment(monthKey, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
        globalModifiers.push({ date, modifier });
      }

      // Save per inventory_id. Serial: the backend deletes-and-reinserts global modifiers
      // on every request that carries them, so parallel requests race on the globals table.
      for (const inventoryId of Array.from(inventoryIds)) {
        const inventoryModifiers: Array<{ date: string; modifier: number }> = [];
        const monthlyMods = salesModifiers.byInventory[inventoryId] || {};
        for (const [monthKey, modifier] of Object.entries(monthlyMods)) {
          const date = moment(monthKey, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
          inventoryModifiers.push({ date, modifier });
        }

        const inventoryOverrides: Array<{ periodKey: string; value: number }> = [];
        const periodOverrides = manualOverrides[inventoryId] || {};
        for (const [periodKey, value] of Object.entries(periodOverrides)) {
          inventoryOverrides.push({ periodKey, value });
        }

        await inventoryService.saveRunRateModifiers({
          inventoryId,
          globalModifiers,
          inventoryModifiers,
          manualOverrides: inventoryOverrides,
        });
      }

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to save settings to API:", error);
    } finally {
      setIsSavingModifiers(false);
    }
  };

  const getEffectiveModifiersForInventory = (inventoryId: string): MonthlySalesModifiers => {
    const effectiveModifiers: MonthlySalesModifiers = {};
    for (const [monthKey, value] of Object.entries(salesModifiers.global)) {
      effectiveModifiers[monthKey] = value;
    }
    const inventoryModifiers = salesModifiers.byInventory[inventoryId];
    if (inventoryModifiers) {
      for (const [monthKey, value] of Object.entries(inventoryModifiers)) {
        effectiveModifiers[monthKey] = value;
      }
    }
    return effectiveModifiers;
  };

  // =========================================================================
  // MANUAL OVERRIDES LOGIC
  // =========================================================================

  const updateManualOverride = (inventoryId: string, periodKey: string, value: number | null) => {
    setManualOverrides((prev) => {
      const newOverrides = { ...prev };
      if (!newOverrides[inventoryId]) {
        newOverrides[inventoryId] = {};
      }
      const inventoryOverrides = { ...newOverrides[inventoryId] };
      if (value === null) {
        delete inventoryOverrides[periodKey];
      } else {
        inventoryOverrides[periodKey] = value;
      }
      if (Object.keys(inventoryOverrides).length === 0) {
        delete newOverrides[inventoryId];
      } else {
        newOverrides[inventoryId] = inventoryOverrides;
      }
      return newOverrides;
    });
  };

  // The pop-up's breakdown needs the product's daily sales series: fetched for that one product
  // when it opens (the page used to load every product's series up front for this).
  const [projectionSeriesStatus, setProjectionSeriesStatus] = useState<{ key: string; loading: boolean; error: string | null } | null>(null);
  const openProjectionDetailDialog = async (inventoryGrouping: InventoryGrouping) => {
    const key = inventoryGrouping.key;
    setProjectionDetailInventoryGrouping({ ...inventoryGrouping, runRate: null });
    setProjectionSeriesStatus({ key, loading: true, error: null });
    try {
      const response: any = await inventoryService.getRunRate({ ...runRateParams(), inventoryId: inventoryGrouping.inventory_id });
      if (!response) throw new Error("Failed to load this product's sales");
      const runRateForCountry = response.resultsByInventoryId?.[inventoryGrouping.inventory_id]?.[inventoryGrouping.country] || null;
      setProjectionDetailInventoryGrouping(current => (current?.key === key ? { ...current, runRate: runRateForCountry } : current));
      setProjectionSeriesStatus(current => (current?.key === key ? { key, loading: false, error: null } : current));
    } catch (error: any) {
      setProjectionSeriesStatus(current => (current?.key === key ? { key, loading: false, error: error?.message || String(error) } : current));
    }
  };

  const closeProjectionDetailDialog = () => {
    setProjectionDetailInventoryGrouping(null);
  };

  // =========================================================================
  // INCOMING POs LOGIC
  // =========================================================================

  /**
   * Derive per-inventory PO view from consolidated POs.
   */
  const incomingPOsByInventory = useMemo(
    () => derivePOsByInventory(consolidatedPOs),
    [consolidatedPOs]
  );

  const getFuturePOs = (inventoryId: string): IncomingPO[] => {
    const today = moment().format("YYYY-MM-DD");
    const pos = incomingPOsByInventory[inventoryId] || [];
    return pos.filter((po) => po.arriveByDate > today);
  };

  const getConfirmedPOs = (inventoryId: string): IncomingPO[] => {
    const pos = incomingPOsByInventory[inventoryId] || [];
    return pos.filter((po) => po.status === 'placed' || !po.status);
  };

  const getDraftPOs = (inventoryId: string): IncomingPO[] => {
    const pos = incomingPOsByInventory[inventoryId] || [];
    return pos.filter((po) => po.status === 'forecasted');
  };

  const handleMarkReceived = async (poNumbers: string[]) => {
    try {
      const result: any = await inventoryService.markPOsReceived(poNumbers);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to mark POs as received:", error);
    }
  };

  const handleUnmarkReceived = async (poNumbers: string[]) => {
    try {
      const result: any = await inventoryService.unmarkPOsReceived(poNumbers);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to unmark POs as received:", error);
    }
  };

  const handleLinkWRO = async (poNumber: string, link: WroLinkRequest) => {
    try {
      const result: any = await inventoryService.linkPOToWRO(poNumber, link);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to link PO to WRO:", error);
    }
  };

  // Same refresh as handleLinkWRO, for the allocation editor (which WRO lines each PO line draws on)
  const handleSetWroAllocations = async (poNumber: string, request: WroAllocationsRequest) => {
    try {
      const result: any = await inventoryService.setWroAllocations(poNumber, request);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to save WRO allocations:", error);
      throw error;
    }
  };

  // WRO-centric write: every PO the WRO feeds comes back refreshed
  const handleSetWroLineAllocations = async (wroId: string, lines: WroLineAllocationsRequest["lines"]) => {
    try {
      const result: any = await inventoryService.setWroLineAllocations(wroId, lines);
      if (result?.pos) {
        setConsolidatedPOs(prev => {
          const updatedMap = new Map(result.pos.map((po: any) => [po.poNumber, po]));
          return prev.map(po => updatedMap.get(po.poNumber) ?? po) as PurchaseOrder[];
        });
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to save WRO allocations:", error);
      throw error;
    }
  };

  const handlePromotePO = async (poNumber: string) => {
    try {
      const result: any = await inventoryService.promotePOToConfirmed(poNumber);
      if (result?.pos) {
        setConsolidatedPOs(result.pos);
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Failed to promote PO:", error);
    }
  };

  const openIncomingPODialog = (inventoryId: string) => {
    po.openDialogWithFilter(inventoryId);
  };

  // =========================================================================
  // INITIAL DATA FETCH
  // =========================================================================

  // /run-rate is no longer fetched for the whole catalog on load: /forecasts already reads the
  // run rates and returns the modifiers it applied (synced below at the moments the old /run-rate
  // fetch fired), and the projection pop-up fetches one product's series when it opens.
  const runRateParams = (): Record<string, any> => {
    const horizonDays = Math.max(0, moment(endDate, "YYYY-MM-DD").diff(moment(), "days"));
    // Determine effective start date - use projectionStartDate if it's earlier than startDate
    let effectiveStartDate = startDate;
    if (runRateBasisMode === "fixed" && fixedRunRateStart && fixedRunRateStart < startDate) {
      effectiveStartDate = fixedRunRateStart;
    }
    const params: Record<string, any> = { startDate: effectiveStartDate, horizonDays };
    if (runRateBasisMode === "fixed" && fixedRunRateStart && fixedRunRateEnd) {
      params.projectionStartDate = fixedRunRateStart;
      params.projectionEndDate = fixedRunRateEnd;
    } else {
      params.basis = `L${runRateBasis}`;
    }
    return params;
  };
  // The modifier editors re-sync from the server exactly when the old /run-rate request re-ran:
  // on these settings and on refreshTrigger — not on bucket or risk changes, which would throw
  // away unsaved modifier edits.
  const runRateSyncKey = [startDate, endDate, runRateBasis, runRateBasisMode, fixedRunRateStart, fixedRunRateEnd, refreshTrigger].join("|");
  const lastRunRateSyncKeyRef = useRef<string | null>(null);

  // The settings /forecasts is asked with; a what-if re-forecasts one product with the same ones
  const forecastSettings = useMemo(() => ({
    startDate,
    endDate,
    basis: `L${runRateBasis}`,
    horizonDays: Math.max(0, moment(endDate, "YYYY-MM-DD").diff(moment(), "days")),
    bucketGranularity,
    riskTimeframe,
    bufferMonths,
    ...(runRateBasisMode === "fixed" && fixedRunRateStart && fixedRunRateEnd
      ? { projectionStartDate: fixedRunRateStart, projectionEndDate: fixedRunRateEnd }
      : {}),
  }), [startDate, endDate, runRateBasis, runRateBasisMode, fixedRunRateStart, fixedRunRateEnd, bucketGranularity, riskTimeframe, bufferMonths]);

  // Effect: Fetch forecasts from server (replaces client-side ledger computation)
  useEffect(() => {
    let isCancelled = false;

    async function loadForecasts(): Promise<void> {
      try {
        setIsLoadingForecasts(true);
        setErrorMessage(null);

        const fresh = forceFreshForecastRef.current;
        forceFreshForecastRef.current = false;
        const forecastsResponse: any = await inventoryService.getForecasts(fresh ? { ...forecastSettings, fresh: true } : forecastSettings);

        if (!forecastsResponse) {
          throw new Error("Failed to load /forecasts");
        }

        if (isCancelled) return;

        setForecastsPayload(forecastsResponse);
        if (forecastsResponse.runRateModifiers && lastRunRateSyncKeyRef.current !== runRateSyncKey) {
          lastRunRateSyncKeyRef.current = runRateSyncKey;
          setRunRatePayload({ runRateModifiers: forecastsResponse.runRateModifiers });
        }
      } catch (error: any) {
        if (!isCancelled) setErrorMessage(error?.message || String(error));
      } finally {
        if (!isCancelled) {
          setIsLoadingForecasts(false);
          setHasForecast(true);
        }
      }
    }

    const timeoutId = setTimeout(() => {
      loadForecasts();
    }, FETCH_SETTLE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  // runRateSyncKey is built from forecastSettings' inputs and refreshTrigger, so it is current
  // whenever this runs; listing it would only duplicate those
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryService, forecastSettings, refreshTrigger, poSaveVersion]);

  // Effect: Load lead times from API
  useEffect(() => {
    let isCancelled = false;

    async function loadLeadTimes() {
      const leadTimesData = await inventoryService.getLeadTimes();
      if (isCancelled) return;
      if (leadTimesData) {
        setLeadTimes(leadTimesData);
      } else {
        setErrorMessage("Failed to load lead times");
      }
    }

    if (!hasForecast) return;
    loadLeadTimes();

    return () => {
      isCancelled = true;
    };
  }, [inventoryService, hasForecast]);

  // Effect: Load OOS alert ignores from API
  useEffect(() => {
    let isCancelled = false;

    async function loadIgnores() {
      try {
        const result = await inventoryService.getOosAlertIgnores();
        if (!isCancelled && result?.ignores) {
          setIgnoredAlertKeys(new Set(
            result.ignores.map((ig: any) => `${ig.inventoryId}||${ig.country}`)
          ));
        }
      } catch {
        // Ignores API may not exist yet
      }
    }

    loadIgnores();

    return () => {
      isCancelled = true;
    };
  }, [inventoryService]);

  const handleToggleIgnore = async (inventoryId: string, country: string) => {
    const key = `${inventoryId}||${country}`;
    const isCurrentlyIgnored = ignoredAlertKeys.has(key);

    setIgnoringKey(key);

    try {
      if (isCurrentlyIgnored) {
        await inventoryService.removeOosAlertIgnore(inventoryId, country);
        setIgnoredAlertKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      } else {
        await inventoryService.addOosAlertIgnore(inventoryId, country);
        setIgnoredAlertKeys(prev => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      }
    } catch (error) {
      console.error("Failed to toggle ignore:", error);
    } finally {
      setIgnoringKey(null);
    }
  };

  // Effect: Load suppliers for cashflow fields in PO dialog
  useEffect(() => {
    let isCancelled = false;

    async function loadSuppliers() {
      try {
        const result = await cashflowService.getSuppliers();
        if (!isCancelled && result) {
          setSuppliers(result.suppliers);
        }
      } catch {
        // Suppliers API may not be available
      }
    }

    if (!hasForecast) return;
    loadSuppliers();

    return () => {
      isCancelled = true;
    };
  }, [cashflowService, hasForecast]);

  // =========================================================================
  // DERIVED DATA (useMemo)
  // =========================================================================

  /**
   * Build inventory groupings directly from server-computed forecasts.
   * The server /forecasts endpoint is the single source of truth for groupings
   * and their ordering (by daily run rate descending).
   */
  // A product sold in several packs shows as one family block: the family row, then its members
  const familyKeyByMemberKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const fg of forecastsPayload?.groupings ?? []) {
      for (const member of fg.family?.members ?? []) map.set(member.key, fg.key);
    }
    return map;
  }, [forecastsPayload]);

  const allInventoryGroupings: InventoryGrouping[] = useMemo(() => {
    if (!forecastsPayload?.groupings) return [];

    return forecastsPayload.groupings
      .map((fg: any) => ({
        key: fg.key,
        inventory_id: fg.inventoryId,
        inventory_name: fg.inventoryName,
        country: fg.country,
        runRate: null,
        current: null,
        history: [],
        skus: fg.skus || [],
        manufacturer: fg.manufacturer,
        brand: fg.brand,
        ddpPrice: fg.ddpPrice,
        manufacturerSku: catalogSuggestions?.manufacturerSkus?.[fg.inventoryId],
        committedQuantity: fg.committedQuantity,
        onhandQuantity: fg.onhandQuantity,
        family: fg.family,
        familyKey: familyKeyByMemberKey.get(fg.key),
        _dailyRunRate: fg.dailyRunRate || 0,
      }))
      .sort((a: any, b: any) => b._dailyRunRate - a._dailyRunRate)
      .map(({ _dailyRunRate, ...rest }: any) => rest);
  }, [forecastsPayload, catalogSuggestions, familyKeyByMemberKey]);

  // The blocks the table pages through: families and stand-alone products (members ride with their family)
  const topLevelGroupings = useMemo(() => allInventoryGroupings.filter(grouping => !grouping.familyKey), [allInventoryGroupings]);
  const membersByFamilyKey = useMemo(() => {
    const map = new Map<string, InventoryGrouping[]>();
    for (const grouping of allInventoryGroupings) {
      if (!grouping.familyKey) continue;
      if (!map.has(grouping.familyKey)) map.set(grouping.familyKey, []);
      map.get(grouping.familyKey)!.push(grouping);
    }
    // The single item first, then packs by how many it holds
    for (const members of map.values()) {
      members.sort((a, b) => (a.family ? 0 : familyMemberQty(a, familyKeyByMemberKey, allInventoryGroupings)) - (b.family ? 0 : familyMemberQty(b, familyKeyByMemberKey, allInventoryGroupings)));
    }
    return map;
  }, [allInventoryGroupings, familyKeyByMemberKey]);

  // Which family blocks are folded to their family row: one person's choice, kept in this browser
  const [collapsedFamilyKeys, setCollapsedFamilyKeys] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_FAMILIES_KEY) || "[]")); } catch { return new Set(); }
  });
  const toggleFamilyCollapsed = (familyKey: string) => {
    setCollapsedFamilyKeys(previous => {
      const next = new Set(previous);
      if (next.has(familyKey)) next.delete(familyKey); else next.add(familyKey);
      try { localStorage.setItem(COLLAPSED_FAMILIES_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore quota */ }
      return next;
    });
  };

  // Rows shrunk to one line (ending inventory only), any row: one person's choice, kept in this browser
  const [collapsedRowKeys, setCollapsedRowKeys] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_ROWS_KEY) || "[]")); } catch { return new Set(); }
  });
  const saveCollapsedRows = (next: Set<string>) => {
    setCollapsedRowKeys(next);
    try { localStorage.setItem(COLLAPSED_ROWS_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore quota */ }
  };
  const toggleRowCollapsed = (groupingKey: string) => {
    const next = new Set(collapsedRowKeys);
    if (next.has(groupingKey)) next.delete(groupingKey); else next.add(groupingKey);
    saveCollapsedRows(next);
  };

  // The page-wide consolidated-row choice: one person's, kept in this browser like the fold above
  const [familyView, setFamilyView] = useState<FamilyView>(() => {
    try {
      const stored = localStorage.getItem(FAMILY_VIEW_KEY);
      return isFamilyView(stored) ? stored : FAMILY_VIEW.SHOW_CONSOLIDATED;
    } catch { return FAMILY_VIEW.SHOW_CONSOLIDATED; }
  });
  const changeFamilyView = (next: FamilyView) => {
    setFamilyView(next);
    setCurrentPageIndex(0);
    try { localStorage.setItem(FAMILY_VIEW_KEY, next); } catch { /* ignore quota */ }
  };

  // Orders that shipped a different pack than usual, by family row key
  const [packSubstitutionsByKey, setPackSubstitutionsByKey] = useState<Map<string, PackSubstitutions["substitutions"][number]>>(new Map());
  useEffect(() => {
    if (!hasForecast) return;
    let isCancelled = false;
    inventoryService.getPackSubstitutions(PACK_SUBSTITUTION_DAYS).then(response => {
      if (isCancelled || !response) return;
      setPackSubstitutionsByKey(new Map(response.substitutions.map(entry => [`${FAMILY_INVENTORY_ID_PREFIX}${entry.baseInventoryId}||${entry.country}`, entry])));
    });
    return () => { isCancelled = true; };
  }, [inventoryService, refreshTrigger, hasForecast]);

  /**
   * Extract run rate modifiers from API.
   */
  const globalRunRateModifiers: DailyRunRateModifiers = useMemo(() => {
    const modifiersArray: GlobalRunRateModifier[] = runRatePayload?.runRateModifiers?.globalModifiers || [];
    const modifiersMap: DailyRunRateModifiers = {};
    for (const modifier of modifiersArray) {
      if (modifier.date && modifier.modifier !== undefined) {
        modifiersMap[modifier.date] = modifier.modifier;
      }
    }
    return modifiersMap;
  }, [runRatePayload]);

  const inventoryRunRateModifiers: Record<string, DailyRunRateModifiers> = useMemo(() => {
    const modifiersArray: InventoryRunRateModifier[] = runRatePayload?.runRateModifiers?.inventoryModifiers || [];
    const modifiersByInventory: Record<string, DailyRunRateModifiers> = {};
    for (const modifier of modifiersArray) {
      if (modifier.date && modifier.inventoryId && modifier.modifier !== undefined) {
        if (!modifiersByInventory[modifier.inventoryId]) {
          modifiersByInventory[modifier.inventoryId] = {};
        }
        modifiersByInventory[modifier.inventoryId][modifier.date] = modifier.modifier;
      }
    }
    return modifiersByInventory;
  }, [runRatePayload]);

  const getEffectiveRunRateModifiers = (inventoryId: string): DailyRunRateModifiers => {
    const effectiveModifiers: DailyRunRateModifiers = { ...globalRunRateModifiers };
    const inventorySpecific = inventoryRunRateModifiers[inventoryId];
    if (inventorySpecific) {
      for (const [date, modifier] of Object.entries(inventorySpecific)) {
        effectiveModifiers[date] = modifier;
      }
    }
    return effectiveModifiers;
  };

  /**
   * Extract unique countries and manufacturers.
   */
  const allCountries = useMemo(() => {
    const countries = new Set<string>();
    for (const grouping of allInventoryGroupings) {
      countries.add(grouping.country);
    }
    return Array.from(countries).sort();
  }, [allInventoryGroupings]);

  const allManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    for (const grouping of allInventoryGroupings) {
      if (grouping.manufacturer) {
        manufacturers.add(grouping.manufacturer);
      }
    }
    return Array.from(manufacturers).sort();
  }, [allInventoryGroupings]);

  // Auto-select all manufacturers (update both staged and applied for initial load)
  useEffect(() => {
    if (allManufacturers.length > 0 && !stagedHasUserInteractedWithManufacturerFilter) {
      const updater = (prev: string[]) => {
        const newManufacturers = allManufacturers.filter(mfr => !prev.includes(mfr));
        if (newManufacturers.length > 0) {
          return [...prev, ...newManufacturers];
        }
        return prev;
      };
      setStagedSelectedManufacturers(updater);
      setAppliedSelectedManufacturers(updater);
    }
  }, [allManufacturers, stagedHasUserInteractedWithManufacturerFilter]);

  const allBrands = useMemo(() => {
    const brands = new Set<string>();
    for (const grouping of allInventoryGroupings) {
      if (grouping.brand) {
        brands.add(grouping.brand);
      }
    }
    return Array.from(brands).sort();
  }, [allInventoryGroupings]);

  // Auto-select all brands (update both staged and applied for initial load)
  useEffect(() => {
    if (allBrands.length > 0 && !stagedHasUserInteractedWithBrandFilter) {
      const updater = (prev: string[]) => {
        const newBrands = allBrands.filter(b => !prev.includes(b));
        if (newBrands.length > 0) {
          return [...prev, ...newBrands];
        }
        return prev;
      };
      setStagedSelectedBrands(updater);
      setAppliedSelectedBrands(updater);
    }
  }, [allBrands, stagedHasUserInteractedWithBrandFilter]);

  // Auto-select all countries except "Unknown" (update both staged and applied for initial load)
  useEffect(() => {
    if (allCountries.length > 0 && !stagedHasUserInteractedWithCountryFilter) {
      const updater = (prev: string[]) => {
        const newCountries = allCountries.filter(country => !prev.includes(country) && country !== "Unknown");
        if (newCountries.length > 0) {
          return [...prev, ...newCountries];
        }
        return prev;
      };
      setStagedSelectedCountries(updater);
      setAppliedSelectedCountries(updater);
    }
  }, [allCountries, stagedHasUserInteractedWithCountryFilter]);

  /**
   * Inventory options for autocomplete in PO dialog.
   */
  const inventoryOptions = useMemo(() => {
    const seen = new Map<string, { id: string; name: string | null; skus: string[] }>();
    for (const grouping of allInventoryGroupings) {
      if (grouping.family) continue; // a family is not something a PO can be written for
      if (!seen.has(grouping.inventory_id)) {
        seen.set(grouping.inventory_id, {
          id: grouping.inventory_id,
          name: grouping.inventory_name,
          skus: grouping.skus || [],
        });
      } else {
        const existing = seen.get(grouping.inventory_id)!;
        if (existing.skus.length === 0 && grouping.skus && grouping.skus.length > 0) {
          existing.skus = grouping.skus;
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.id.localeCompare(b.id));
  }, [allInventoryGroupings]);

  // ── SKU master prices (pre-fill for PO line unit prices) ──
  const [priceChangesToOffer, setPriceChangesToOffer] = useState<{ poNumber: string; changes: PriceChange[]; productNumberChanges: ProductNumberChange[] } | null>(null);
  const [generatingPdfPoNumber, setGeneratingPdfPoNumber] = useState<string | null>(null);

  useEffect(() => {
    if (!hasForecast) return;
    let isCancelled = false;
    inventoryService.getCatalogPrices().then(response => {
      if (isCancelled || !response) return;
      setCatalogSuggestions(response);
    });
    return () => { isCancelled = true; };
  }, [inventoryService, hasForecast]);


  const handleSuggestPoNumber = async () => {
    const suggestion = await inventoryService.getNextPoNumber(po.editingPOCountryOfDestination);
    if (suggestion) {
      po.dialogProps.onSetPONumber(suggestion.poNumber);
    } else {
      setErrorMessage("Failed to suggest a PO number");
    }
  };

  const handleGeneratePdf = async (poToPrint: PurchaseOrder) => {
    setGeneratingPdfPoNumber(poToPrint.poNumber);
    try {
      const blob = await inventoryService.downloadPoPdf(poToPrint.poNumber);
      if (blob) {
        downloadBlob(blob, `${poToPrint.poNumber.replace(/[^A-Za-z0-9._-]/g, "_")}.pdf`);
      } else {
        setErrorMessage(`Failed to generate the PDF for ${poToPrint.poNumber}`);
      }
    } finally {
      setGeneratingPdfPoNumber(null);
    }
  };

  /**
   * Fetch ALL POs via /incoming-pos so the PO management dialog shows the
   * complete set (matching the Cashflow tool).
   */
  // POs and WROs load alongside the forecast rather than after it: they used to wait for the
  // forecast's product list (for display names) and then for each other. Names come from the
  // server's SKU master join; the effect below prefers the product list's names once it is in.
  const fetchAllPOs = useCallback(async () => {
    const [result, wroResult]: any[] = await Promise.all([
      inventoryService.getIncomingPOs().catch((error: any) => { console.error("Failed to fetch all POs:", error); return null; }),
      inventoryService.getWROs().catch((error: any) => { console.error("Failed to fetch WROs:", error); return null; }),
    ]);
    if (wroResult?.wros) setWros(wroResult.wros as WRO[]);
    try {
      if (!result?.pos) return;

      const allPOs = result.pos as any[];
      const mapped: PurchaseOrder[] = allPOs.map((po: any) => ({
        poNumber: po.poNumber,
        arriveByDate: po.arriveByDate,
        description: po.description || "",
        lineItems: (po.lineItems || []).map((li: any) => ({
          inventoryId: li.inventoryId,
          inventoryName: li.inventoryName || undefined,
          sku: li.sku ?? null,
          quantity: li.quantity,
          unitPrice: li.unitPrice ?? null,
          manufacturerProductNumber: li.manufacturerProductNumber ?? null,
          skuConfirmedAt: li.skuConfirmedAt ?? null,
          ...(li.received != null && { received: li.received }),
          ...(li.receivedSource !== undefined && { receivedSource: li.receivedSource }),
          ...(li.incomingQuantity != null && { incomingQuantity: li.incomingQuantity }),
          ...(li.projectedArrivalDate != null && { projectedArrivalDate: li.projectedArrivalDate }),
          ...(li.wroIds != null && { wroIds: li.wroIds }),
          ...(li.wroStatus != null && { wroStatus: li.wroStatus }),
          ...(li.wroExpectedQuantity != null && { wroExpectedQuantity: li.wroExpectedQuantity }),
          ...(li.wroLinks != null && { wroLinks: li.wroLinks }),
          ...(li.orphanedAllocations != null && { orphanedAllocations: li.orphanedAllocations }),
          ...(li.stowedQuantity != null && { stowedQuantity: li.stowedQuantity }),
          ...(li.stowedAt != null && { stowedAt: li.stowedAt }),
        })),
        ...(po.projectedArrivalDate && { projectedArrivalDate: po.projectedArrivalDate }),
        ...(po.wroLinkSource && { wroLinkSource: po.wroLinkSource }),
        ...(po.pinnedWroId !== undefined && { pinnedWroId: po.pinnedWroId }),
        ...(po.amount != null && { amount: po.amount }),
        ...(po.supplierId != null && { supplierId: po.supplierId }),
        ...(po.tariffRate != null && { tariffRate: po.tariffRate }),
        ...(po.status && { status: po.status }),
        ...(po.brand && { brand: po.brand }),
        ...(po.placementDate && { placementDate: po.placementDate }),
        ...(po.etd && { etd: po.etd }),
        ...(po.countryOfDestination && { countryOfDestination: po.countryOfDestination }),
        ...(po.paymentDateOverrides && { paymentDateOverrides: po.paymentDateOverrides }),
        ...(po.received != null && { received: po.received }),
        ...(po.paidTranches != null && { paidTranches: po.paidTranches }),
        ...(po.unitPrice != null && { unitPrice: po.unitPrice }),
        ...(po.priceType && { priceType: po.priceType }),
        ...(po.containerType && { containerType: po.containerType }),
      }));

      setConsolidatedPOs(mapped.sort((a, b) => a.arriveByDate.localeCompare(b.arriveByDate)));
    } catch (error) {
      console.error("Failed to fetch all POs:", error);
    }
  }, [inventoryService]);

  // On load and after every PO / WRO action (they bump refreshTrigger or poSaveVersion)
  useEffect(() => {
    if (!hasForecast) return;
    fetchAllPOs();
  }, [fetchAllPOs, refreshTrigger, poSaveVersion, hasForecast]);

  // The product list's display name wins over the server's, as it always has
  useEffect(() => {
    if (inventoryOptions.length === 0) return;
    const nameById = new Map(inventoryOptions.map(opt => [opt.id, opt.name]));
    setConsolidatedPOs(previous => {
      let changed = false;
      const next = previous.map(po => ({
        ...po,
        lineItems: po.lineItems.map(li => {
          const name = nameById.get(li.inventoryId);
          if (!name || name === li.inventoryName) return li;
          changed = true;
          return { ...li, inventoryName: name };
        }),
      }));
      return changed ? next : previous;
    });
  }, [inventoryOptions, consolidatedPOs]);

  // =========================================================================
  // CONSOLIDATED PO MANAGEMENT
  // =========================================================================

  const po = usePOManagement({
    inventoryOptions,
    suppliers,
    onSave: async (params: POSaveParams) => {
      const poNumberChanged = params.originalPONumber && params.originalPONumber !== params.poNumber;

      const newPO: PurchaseOrder = {
        poNumber: params.poNumber,
        arriveByDate: params.arriveByDate,
        description: params.description,
        lineItems: params.lineItems,
        amount: params.amount,
        supplierId: params.supplierId,
        tariffRate: params.tariffRatePercent != null ? params.tariffRatePercent / 100 : null,
        status: params.status,
        brand: params.brand,
        placementDate: params.placementDate,
        etd: params.etd,
        countryOfDestination: params.countryOfDestination,
        paymentDateOverrides: params.paymentDateOverrides,
        priceType: params.priceType,
        containerType: params.containerType,
        shipToFcId: params.shipToFcId,
      };

      // Optimistic update (incomingPOsByInventory auto-derives via useMemo)
      setConsolidatedPOs(prev => {
        const filtered = prev.filter(p =>
          p.poNumber !== params.poNumber && (!poNumberChanged || p.poNumber !== params.originalPONumber)
        );
        return [...filtered, newPO].sort((a, b) => a.arriveByDate.localeCompare(b.arriveByDate));
      });

      try {
        const posToSave = newPO.lineItems.map(lineItem => ({
          inventoryId: lineItem.inventoryId,
          poNumber: newPO.poNumber,
          arriveByDate: newPO.arriveByDate,
          quantity: lineItem.quantity,
          description: newPO.description,
          placementDate: params.placementDate,
          etd: params.etd,
          amount: params.amount,
          supplierId: params.supplierId,
          tariffRate: lineItem.tariffRate ?? null,
          status: params.status,
          brand: params.brand,
          countryOfDestination: params.countryOfDestination,
          paymentDateOverrides: params.paymentDateOverrides,
          unitPrice: lineItem.unitPrice ?? null,
          priceType: params.priceType,
          containerType: params.containerType,
          shipToFcId: params.shipToFcId,
          manufacturerProductNumber: lineItem.manufacturerProductNumber ?? null,
          skuConfirmed: Boolean(lineItem.skuConfirmed ?? lineItem.skuConfirmedAt),
        }));

        await persistPO(inventoryService, params, posToSave);
      } catch (error: any) {
        console.error("Failed to save consolidated PO:", error);
        setErrorMessage(`PO ${params.poNumber} was not saved: ${error?.message || error}`);
        // Re-fetch (POs and forecast) to revert the optimistic update so the UI shows what the server
        // actually has (a failed save can still have renamed the PO or removed lines before it stopped)
        setPoSaveVersion(prev => prev + 1);
        return;
      }
      // Re-fetch the POs and the forecast table, which is computed from the saved POs on the server
      // (without this a changed quantity only showed up in the PO lists)
      setPoSaveVersion(prev => prev + 1);

      po.markSaved();
      const changes = findPriceChanges(params.lineItems, params.containerType, catalogSuggestions);
      const productNumberChanges = findProductNumberChanges(params.lineItems, catalogSuggestions);
      if (changes.length > 0 || productNumberChanges.length > 0) setPriceChangesToOffer({ poNumber: params.poNumber, changes, productNumberChanges });
    },
    onDelete: async (poToDelete: PurchaseOrder) => {
      const poNumber = poToDelete.poNumber;

      for (const lineItem of poToDelete.lineItems) {
        const deleted = await inventoryService.deleteIncomingPO(lineItem.inventoryId, poNumber);
        if (!deleted) {
          setErrorMessage(`Failed to delete PO ${poNumber} line item ${lineItem.inventoryId}`);
          break;
        }
      }

      // Re-fetch all POs and the forecast they feed
      setPoSaveVersion(prev => prev + 1);
    },
  });

  // =========================================================================
  // FILTER & PAGINATION
  // =========================================================================

  const filteredInventoryGroupings = useMemo(() => {
    const normalizedFilterText = appliedInventoryFilterText.trim().toLowerCase();

    const passesFilters = (inventoryGrouping: InventoryGrouping): boolean => {
      const inventorySearchableText = `${inventoryGrouping.inventory_id} ${inventoryGrouping.inventory_name || ""} ${inventoryGrouping.manufacturerSku || ""}`.toLowerCase();
      const matchesSearchFilter = !normalizedFilterText ||
        inventorySearchableText.includes(normalizedFilterText) ||
        (inventoryGrouping.skus || []).some(sku => sku.toLowerCase().includes(normalizedFilterText));

      const matchesCountryFilter =
        appliedSelectedCountries.length === 0 ||
        appliedSelectedCountries.includes(inventoryGrouping.country);

      const matchesManufacturerFilter =
        !appliedHasUserInteractedWithManufacturerFilter ||
        appliedSelectedManufacturers.length === 0 ||
        (inventoryGrouping.manufacturer && appliedSelectedManufacturers.includes(inventoryGrouping.manufacturer));

      const matchesBrandFilter =
        !appliedHasUserInteractedWithBrandFilter ||
        appliedSelectedBrands.length === 0 ||
        (inventoryGrouping.brand && appliedSelectedBrands.includes(inventoryGrouping.brand));

      return Boolean(matchesSearchFilter && matchesCountryFilter && matchesManufacturerFilter && matchesBrandFilter);
    };

    return topLevelGroupings.filter(grouping =>
      passesFilters(grouping) || (membersByFamilyKey.get(grouping.key) ?? []).some(passesFilters));
  }, [topLevelGroupings, membersByFamilyKey, appliedInventoryFilterText, appliedSelectedCountries, appliedSelectedManufacturers, appliedSelectedBrands, appliedHasUserInteractedWithCountryFilter, appliedHasUserInteractedWithManufacturerFilter, appliedHasUserInteractedWithBrandFilter]);

  // What the projections table pages through. With the consolidated rows hidden, each SKU of a
  // family stands alone, so it drops its family link for display. Only the table: the risk
  // summary and dashboard keep using the filtered list above.
  const tableTopLevelGroupings = useMemo(() => {
    if (familyView === FAMILY_VIEW.HIDE_CONSOLIDATED) {
      return filteredInventoryGroupings.flatMap(grouping => (grouping.family
        ? (membersByFamilyKey.get(grouping.key) ?? []).filter(member => !member.family).map(member => ({ ...member, familyKey: undefined }))
        : [grouping]));
    }
    return filteredInventoryGroupings;
  }, [filteredInventoryGroupings, membersByFamilyKey, familyView]);

  const filteredInventoryGroupingsWithMembers = useMemo(
    () => filteredInventoryGroupings.flatMap(grouping => [grouping, ...(membersByFamilyKey.get(grouping.key) ?? [])]),
    [filteredInventoryGroupings, membersByFamilyKey],
  );

  // =========================================================================
  // RISK SUMMARY (server-computed)
  // =========================================================================

  /**
   * Map server-computed risk items to frontend InventoryRiskItem type.
   * Respects current frontend filters.
   */
  const riskSummaryItems: InventoryRiskItem[] = useMemo(() => {
    if (!forecastsPayload?.riskSummary) return [];

    const { critical = [], atRisk = [], watch = [] } = forecastsPayload.riskSummary;
    const allRiskItems = [...critical, ...atRisk, ...watch];

    // Build a set of filtered grouping keys for filtering
    const filteredKeys = new Set(filteredInventoryGroupingsWithMembers.map(g => g.key));

    // Map server camelCase to frontend expected shape and filter
    return allRiskItems
      .filter((item: any) => filteredKeys.has(item.groupingKey))
      .map((item: any) => ({
        inventory_id: item.inventoryId,
        inventory_name: item.inventoryName,
        country: item.country,
        skus: item.skus,
        currentInventory: item.currentInventory,
        dailyRunRate: item.dailyRunRate,
        demandUnitsSold: item.demandUnitsSold,
        demandInStockDays: item.demandInStockDays,
        demandDailyRate: item.demandDailyRate,
        projectedStockOutDate: item.projectedStockOutDate,
        daysUntilStockOut: item.daysUntilStockOut,
        leadTimeMonths: item.leadTimeMonths,
        leadTimeSource: item.leadTimeSource,
        leadTimeCountryAdjustmentMonths: item.leadTimeCountryAdjustmentMonths,
        bufferMonths: item.bufferMonths,
        recommendedOrderDate: item.recommendedOrderDate,
        daysUntilOrderNeeded: item.daysUntilOrderNeeded,
        projectedSalesInPeriod: item.projectedSalesInPeriod,
        severity: item.severity,
        groupingKey: item.groupingKey,
      }));
  }, [forecastsPayload, filteredInventoryGroupingsWithMembers]);

  /**
   * Save a per-SKU lead time override (months), then refresh forecasts so the
   * recommended order dates reflect it.
   */
  const updateLeadTime = async (inventoryId: string, newLeadTimeMonths: number) => {
    const result = await inventoryService.saveLeadTimes({
      inventoryLeadTimes: [{ inventoryId, leadTimeMonths: newLeadTimeMonths }],
    });
    if (!result) {
      setErrorMessage(`Failed to save lead time for ${inventoryId}`);
      return;
    }
    setLeadTimes(result);
    setRefreshTrigger((prev) => prev + 1);
  };

  /**
   * Save a manufacturer-level default lead time (months), then refresh forecasts.
   */
  const updateManufacturerLeadTime = async (manufacturer: string, newLeadTimeMonths: number) => {
    const result = await inventoryService.saveLeadTimes({
      manufacturerLeadTimes: [{ manufacturer, leadTimeMonths: newLeadTimeMonths }],
    });
    if (!result) {
      setErrorMessage(`Failed to save lead time for ${manufacturer}`);
      return;
    }
    setLeadTimes(result);
    setRefreshTrigger((prev) => prev + 1);
  };

  /**
   * Save a country-level adjustment (± months on top of the base lead time),
   * then refresh forecasts.
   */
  const updateCountryAdjustment = async (country: string, adjustmentMonths: number) => {
    const result = await inventoryService.saveLeadTimes({
      countryAdjustments: [{ country, adjustmentMonths }],
    });
    if (!result) {
      setErrorMessage(`Failed to save lead time adjustment for ${country}`);
      return;
    }
    setLeadTimes(result);
    setRefreshTrigger((prev) => prev + 1);
  };

  /**
   * Remove a manufacturer default, per-SKU override, or country adjustment,
   * then refresh forecasts.
   */
  const removeLeadTime = async (target: { manufacturer?: string; inventoryId?: string; country?: string }) => {
    const result = await inventoryService.deleteLeadTime(target);
    if (!result) {
      setErrorMessage("Failed to remove lead time");
      return;
    }
    setLeadTimes(result);
    setRefreshTrigger((prev) => prev + 1);
  };

  /**
   * Scroll to and highlight a row in the inventory table.
   * If the row is not on the current page, apply the inventory_id as filter.
   */
  const scrollToTableRow = (groupingKey: string) => {
    const rowElement = tableRowRefs.current.get(groupingKey);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Add highlight animation
      rowElement.style.backgroundColor = "#fff3e0";
      setTimeout(() => {
        rowElement.style.transition = "background-color 1s ease";
        rowElement.style.backgroundColor = "";
      }, 100);
      setTimeout(() => {
        rowElement.style.transition = "";
      }, 1100);
    } else {
      // Row not on current page - extract inventory_id and apply as filter
      // groupingKey format: `${inventory_id}||${country}`
      const inventoryId = groupingKey.split("||")[0];
      if (inventoryId) {
        setStagedInventoryFilterText(inventoryId);
        setAppliedInventoryFilterText(inventoryId);
        setCurrentPageIndex(0);
      }
    }
  };

  // =========================================================================
  // PAGINATION
  // =========================================================================

  useEffect(() => {
    setCurrentPageIndex(0);
  }, [bucketGranularity, groupingsPerPage, appliedInventoryFilterText, appliedSelectedCountries, appliedSelectedManufacturers, startDate, endDate]);

  const pinnedKeys = useMemo(() => new Set(productArrangement.pinnedKeys), [productArrangement.pinnedKeys]);

  // Pinned products head every page whatever the filters say: they are there to compare against
  const pinnedInventoryGroupings = useMemo(() => {
    const groupingsByKey = new Map(allInventoryGroupings.map(grouping => [grouping.key, grouping]));
    return productArrangement.pinnedKeys.flatMap(key => groupingsByKey.get(key) ?? [])
      // A pinned family is not shown while families are hidden or split into their packs
      .filter(grouping => familyView === FAMILY_VIEW.SHOW_CONSOLIDATED || !grouping.family);
  }, [allInventoryGroupings, productArrangement.pinnedKeys, familyView]);

  const sortedInventoryGroupings = useMemo(
    () => sortGroupings(tableTopLevelGroupings, productArrangement).filter(grouping => !pinnedKeys.has(grouping.key)),
    [tableTopLevelGroupings, productArrangement, pinnedKeys],
  );

  const updateProductArrangement = (nextArrangement: ProductArrangement) => {
    setViewUndoStack(stack => [...stack, { arrangement: productArrangement, pageIndex: currentPageIndex }].slice(-VIEW_UNDO_LIMIT));
    setProductArrangement(nextArrangement);
    saveProductArrangement(nextArrangement);
  };

  const undoViewChange = () => {
    const previousView = viewUndoStack[viewUndoStack.length - 1];
    if (!previousView) return;
    setViewUndoStack(viewUndoStack.slice(0, -1));
    setProductArrangement(previousView.arrangement);
    saveProductArrangement(previousView.arrangement);
    setCurrentPageIndex(previousView.pageIndex);
  };

  // Cmd/Ctrl+Z undoes the last change to the view, except while typing, where it belongs to the text box
  const undoViewChangeRef = useRef(undoViewChange);
  undoViewChangeRef.current = undoViewChange;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey || event.key.toLowerCase() !== "z") return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      // A dialog is open: the shortcut is not about the table behind it
      if (document.querySelector('[role="dialog"]')) return;
      event.preventDefault();
      undoViewChangeRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleProductSortChange = (nextSort: ProductSort) => {
    updateProductArrangement({ ...productArrangement, sort: nextSort });
    setCurrentPageIndex(0);
  };

  const handleTogglePin = (groupingKey: string) => {
    if (!pinnedKeys.has(groupingKey)) {
      // Pinning takes the product out of the pages; don't strand the view on a page that emptied
      const lastPageIndex = Math.max(0, Math.ceil((sortedInventoryGroupings.length - 1) / groupingsPerPage) - 1);
      setCurrentPageIndex(pageIndex => Math.min(pageIndex, lastPageIndex));
    }
    updateProductArrangement({
      ...productArrangement,
      pinnedKeys: pinnedKeys.has(groupingKey)
        ? productArrangement.pinnedKeys.filter(key => key !== groupingKey)
        : [...productArrangement.pinnedKeys, groupingKey],
    });
  };

  const handleMoveProduct = (movedKey: string, targetKey: string, position: MovePosition) => {
    if (pinnedKeys.has(movedKey) !== pinnedKeys.has(targetKey)) return;
    if (pinnedKeys.has(movedKey)) {
      updateProductArrangement({ ...productArrangement, pinnedKeys: moveKey(productArrangement.pinnedKeys, movedKey, targetKey, position) });
      return;
    }
    // The hand-made order starts from whatever order is on screen, over every product (not only
    // the filtered ones), so a move made under a filter keeps its place once the filter is lifted.
    const currentOrder = sortGroupings(allInventoryGroupings, productArrangement).map(grouping => grouping.key);
    updateProductArrangement({
      ...productArrangement,
      sort: PRODUCT_SORT.CUSTOM,
      customOrder: moveKey(currentOrder, movedKey, targetKey, position),
    });
    // Follow the product when an arrow carries it onto the neighbouring page
    const visibleOrder = moveKey(sortedInventoryGroupings.map(grouping => grouping.key), movedKey, targetKey, position);
    setCurrentPageIndex(Math.floor(visibleOrder.indexOf(movedKey) / groupingsPerPage));
  };

  const productRowControls: ProductRowControls = useMemo(() => {
    const neighborsByKey = new Map<string, ProductNeighbors>();
    for (const groupings of [pinnedInventoryGroupings, sortedInventoryGroupings]) {
      groupings.forEach((grouping, index) => neighborsByKey.set(grouping.key, {
        previousKey: groupings[index - 1]?.key ?? null,
        nextKey: groupings[index + 1]?.key ?? null,
      }));
    }
    return {
      pinnedKeys, neighborsByKey, onTogglePin: handleTogglePin, onMoveProduct: handleMoveProduct,
      collapsedRowKeys, onToggleRowCollapsed: toggleRowCollapsed,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedKeys, pinnedInventoryGroupings, sortedInventoryGroupings, productArrangement, groupingsPerPage, currentPageIndex, collapsedRowKeys]);

  const totalPages = Math.max(1, Math.ceil(sortedInventoryGroupings.length / groupingsPerPage));

  const pagedInventoryGroupings = useMemo(() => [
    ...pinnedInventoryGroupings,
    ...sortedInventoryGroupings.slice(currentPageIndex * groupingsPerPage, currentPageIndex * groupingsPerPage + groupingsPerPage),
  ].flatMap(grouping => (grouping.family && !collapsedFamilyKeys.has(grouping.key)
    ? [grouping, ...(membersByFamilyKey.get(grouping.key) ?? [])]
    : [grouping])),
  [pinnedInventoryGroupings, sortedInventoryGroupings, currentPageIndex, groupingsPerPage, collapsedFamilyKeys, membersByFamilyKey]);

  // Collapse all: every row the table can show under the current filters, on every page (pinned
  // products and the SKUs of each family included). Expand all opens every collapsed row.
  const collapseAllRows = () => {
    const keys = new Set(collapsedRowKeys);
    for (const grouping of [...pinnedInventoryGroupings, ...sortedInventoryGroupings]) {
      keys.add(grouping.key);
      if (grouping.family) for (const member of membersByFamilyKey.get(grouping.key) ?? []) keys.add(member.key);
    }
    saveCollapsedRows(keys);
  };
  const expandAllRows = () => saveCollapsedRows(new Set());

  const familyControls: FamilyControls = useMemo(() => ({
    collapsedFamilyKeys,
    onToggleCollapsed: toggleFamilyCollapsed,
    substitutionsByFamilyKey: packSubstitutionsByKey,
    substitutionDays: PACK_SUBSTITUTION_DAYS,
  }), [collapsedFamilyKeys, packSubstitutionsByKey]);

  // =========================================================================
  // PERIOD COLUMNS & METRIC VALUES (from server-computed forecasts)
  // =========================================================================

  // =========================================================================
  // DEMAND WHAT-IFS (unsaved adjustments, re-forecast one product at a time)
  // =========================================================================

  const currentDemandSettings: DemandSettingsByInventory = useMemo(
    () => ({ byInventory: salesModifiers.byInventory, manualOverrides }),
    [salesModifiers.byInventory, manualOverrides],
  );

  // The rows on screen, plus the one open in the projection dialog
  const whatIfVisibleGroupings = useMemo(() => {
    const dialogGrouping = projectionDetailInventoryGrouping;
    return dialogGrouping && !pagedInventoryGroupings.some(grouping => grouping.key === dialogGrouping.key)
      ? [...pagedInventoryGroupings, dialogGrouping]
      : pagedInventoryGroupings;
  }, [pagedInventoryGroupings, projectionDetailInventoryGrouping]);

  const demandWhatIf = useDemandWhatIf({
    inventoryService,
    forecastSettings,
    // Both only ever increase, so their sum changes whenever either does
    dataVersion: refreshTrigger + poSaveVersion,
    visibleGroupings: whatIfVisibleGroupings,
    current: currentDemandSettings,
    saved: savedDemandSettings,
  });

  /** Drop a product's unsaved adjustments (and any trial daily rate on its rows): back to what is saved */
  const discardWhatIf = (inventoryId: string) => {
    const saved = productDemandSettings(savedDemandSettings, inventoryId);
    const restore = <T extends Record<string, unknown>>(all: Record<string, T>, savedForProduct: T) => {
      const { [inventoryId]: _dropped, ...others } = all;
      return Object.keys(savedForProduct).length > 0 ? { ...others, [inventoryId]: savedForProduct } : others;
    };
    setSalesModifiers(previous => ({ ...previous, byInventory: restore(previous.byInventory, saved.salesModifiers) }));
    setManualOverrides(previous => restore(previous, saved.manualOverrides));
    for (const grouping of allInventoryGroupings) {
      if (grouping.inventory_id === inventoryId) demandWhatIf.setTrialRate(grouping.key, null);
    }
  };

  /**
   * Save a product's adjustments for everyone, then re-forecast just that product (every country
   * it is stocked in) from what the server now holds. No page reload.
   */
  const saveWhatIf = async (inventoryId: string) => {
    const settings = productDemandSettings(currentDemandSettings, inventoryId);
    // A failure here is a failed save: the dialog shows it and nothing changed
    await inventoryService.saveProductDemandSettings(inventoryId, settings);
    setSavedDemandSettings(previous => ({
      byInventory: { ...previous.byInventory, [inventoryId]: settings.salesModifiers },
      manualOverrides: { ...previous.manualOverrides, [inventoryId]: settings.manualOverrides },
    }));

    // Saved. From here on only the table's refresh can fail, and a full reload covers that.
    const productGroupings = allInventoryGroupings.filter(grouping => grouping.inventory_id === inventoryId);
    // A family row sums its members server-side, so a member's change needs the whole /forecasts
    if (productGroupings.some(grouping => grouping.familyKey)) {
      setRefreshTrigger((previous) => previous + 1);
      return;
    }
    try {
      const refreshed = await Promise.all(productGroupings.map(grouping =>
        inventoryService.simulateForecast({ ...forecastSettings, inventoryId, country: grouping.country, whatIf: {} })));
      setForecastsPayload((payload: any) => applyForecastUpdates(payload, refreshed.map(simulation => ({ forecast: simulation.baseline, riskItem: simulation.simulatedRiskItem }))));
    } catch (error) {
      console.error(`Saved ${inventoryId}, but re-forecasting it failed; reloading the page's forecast instead`, error);
      setRefreshTrigger((previous) => previous + 1);
    }
  };

  const whatIfControls: WhatIfControls = useMemo(() => ({
    simulationsByKey: demandWhatIf.simulationsByKey,
    unsavedInventoryIds: demandWhatIf.unsavedIds,
    onDiscard: discardWhatIf,
    onSave: setSavingWhatIfInventoryId,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [demandWhatIf.simulationsByKey, demandWhatIf.unsavedIds, savedDemandSettings, allInventoryGroupings]);

  /** Build a lookup from grouping key to forecast result; a finished what-if stands in for its row */
  const forecastsByKey = useMemo(() => {
    const map = new Map<string, any>();
    if (forecastsPayload?.groupings) {
      for (const g of forecastsPayload.groupings) {
        map.set(g.key, demandWhatIf.simulationsByKey[g.key]?.result?.simulated ?? g);
      }
    }
    return map;
  }, [forecastsPayload, demandWhatIf.simulationsByKey]);

  const periodColumns: PeriodColumn[] = useMemo(() => {
    const periodMap = new Map<string, PeriodColumn>();

    for (const inventoryGrouping of pagedInventoryGroupings) {
      const forecast = forecastsByKey.get(inventoryGrouping.key);
      const aggregatedPeriods = forecast?.aggregatedLedger || [];

      for (const periodAggregate of aggregatedPeriods) {
        if (!periodMap.has(periodAggregate.key)) {
          periodMap.set(periodAggregate.key, {
            key: periodAggregate.key,
            label:
              bucketGranularity === "day"
                ? periodAggregate.key
                : `${periodAggregate.key} (${periodAggregate.periodStart} → ${periodAggregate.periodEnd})`,
            periodStart: periodAggregate.periodStart,
            periodEnd: periodAggregate.periodEnd,
          });
        }
      }
    }

    return Array.from(periodMap.values()).sort((leftColumn, rightColumn) =>
      leftColumn.periodStart < rightColumn.periodStart ? -1 : 1,
    );
  }, [pagedInventoryGroupings, forecastsByKey, bucketGranularity]);

  const metricValuesByGroupingKey: Map<string, Map<string, PeriodMetrics>> = useMemo(() => {
    const result = new Map<string, Map<string, PeriodMetrics>>();
    const todayStr = moment().format("YYYY-MM-DD");
    const currentMonthKey = moment().format("YYYY-MM");

    for (const inventoryGrouping of pagedInventoryGroupings) {
      const forecast = forecastsByKey.get(inventoryGrouping.key);
      const aggregatedPeriods = forecast?.aggregatedLedger || [];
      const periodAggregateByKey = new Map(
        aggregatedPeriods.map((periodAggregate: any) => [periodAggregate.key, periodAggregate]),
      );

      // Determine current stock from forecast data
      const currentStock = forecast?.currentInventory ?? null;
      const inventoryValue = (currentStock != null && inventoryGrouping.ddpPrice != null)
        ? Math.round(currentStock * inventoryGrouping.ddpPrice)
        : null;

      // Get modifiers for breakdown tooltips
      const inventoryId = inventoryGrouping.inventory_id;
      const invModifiers = salesModifiers.byInventory[inventoryId] || {};
      const invManualOverrides = manualOverrides[inventoryId] || {};

      // Build sorted period keys for prev-month lookups from server data
      const sortedAggKeys = [...periodAggregateByKey.keys()].sort();

      const valuesForGrouping = new Map<string, PeriodMetrics>();

      for (const periodColumn of periodColumns) {
        const periodAggregate: any = periodAggregateByKey.get(periodColumn.key);
        const periodContainsToday = periodColumn.periodStart <= todayStr && periodColumn.periodEnd >= todayStr;
        const isFuturePeriod = periodAggregate?.isProjected === true || periodColumn.periodStart > todayStr;

        // Build sold breakdown tooltip using actual server values
        let soldBreakdown: string | null = null;
        if (isFuturePeriod && periodAggregate) {
          const mKey = periodColumn.key;
          const modifier = invModifiers[mKey] ?? salesModifiers.global[mKey] ?? undefined;
          const override = invManualOverrides[mKey];
          const actualSold = Math.round(periodAggregate.sold);

          const modStr = modifier != null && modifier !== 1 ? ` × ${modifier}x` : "";
          const daysInPeriod = moment(periodColumn.periodEnd).diff(moment(periodColumn.periodStart), "days") + 1;
          const basisRate: number | undefined = forecast?.projection?.unitsPerDay;

          if (inventoryGrouping.family) {
            // A family row is its members added up in base units; each member explains itself on its own row
            const parts = inventoryGrouping.family.members.map(member => {
              const memberSold = Math.round(forecastsByKey.get(member.key)?.aggregatedLedger?.find((p: any) => p.key === periodColumn.key)?.sold ?? 0);
              const label = member.qty > 1 ? `${member.qty}-pack` : "single";
              return member.qty > 1 ? `${member.qty} × ${memberSold.toLocaleString()} (${label})` : `${memberSold.toLocaleString()} (${label})`;
            });
            soldBreakdown = `${parts.join(" + ")} = ${actualSold.toLocaleString()}`;
          } else if (override !== undefined) {
            soldBreakdown = `Manual Override: ${Math.round(override)} units`;
          } else if (mKey === currentMonthKey || periodContainsToday) {
            const impliedRate = daysInPeriod > 0 ? (actualSold / daysInPeriod).toFixed(1) : "0";
            soldBreakdown = `~${impliedRate} units/day × ${daysInPeriod} days${modStr} = ${actualSold.toLocaleString()}`;
          } else if (mKey === moment(currentMonthKey, "YYYY-MM").add(1, "month").format("YYYY-MM") && basisRate != null) {
            // Next month is still the basis rate × modifier; compounding on the previous month starts the month after
            soldBreakdown = `Basis rate ${basisRate.toFixed(1)} units/day × ${daysInPeriod} days${modStr} = ${actualSold.toLocaleString()}`;
          } else {
            // Month after next onward: previous month's projected total × this month's modifier
            const currentIdx = sortedAggKeys.indexOf(mKey);
            const prevKey = currentIdx > 0 ? sortedAggKeys[currentIdx - 1] : null;
            const prevAgg: any = prevKey ? periodAggregateByKey.get(prevKey) : null;
            const prevSold = prevAgg ? Math.round(prevAgg.sold ?? 0) : null;
            if (prevSold != null) {
              const modVal = modifier ?? 1.0;
              soldBreakdown = `Prev month (${prevSold.toLocaleString()}) × ${modVal}x = ${actualSold.toLocaleString()}`;
            }
          }
        }

        // Build ending inventory breakdown
        let endingBreakdown: string | null = null;
        if (periodAggregate) {
          const start = Math.round(periodAggregate.startingInventory);
          const sold = Math.round(periodAggregate.sold);
          const added = Math.round(periodAggregate.inventoryAdded);
          const ending = Math.round(periodAggregate.endingInventory);
          const addedStr = added > 0 ? ` + ${added} incoming` : "";
          endingBreakdown = `${start} starting − ${sold} sold${addedStr} = ${ending}`;
        }

        valuesForGrouping.set(periodColumn.key, {
          startingInventory: periodAggregate ? Math.round(periodAggregate.startingInventory) : null,
          soldQuantity: periodAggregate ? Math.round(periodAggregate.sold) : null,
          inventoryAdded: periodAggregate ? Math.round(periodAggregate.inventoryAdded) : null,
          confirmedInventoryAdded: periodAggregate ? Math.round(periodAggregate.confirmedInventoryAdded) : null,
          draftInventoryAdded: periodAggregate ? Math.round(periodAggregate.draftInventoryAdded) : null,
          miscAdjustment: periodAggregate ? Math.round(periodAggregate.miscAdjustment) : null,
          endingInventory: periodAggregate ? Math.round(periodAggregate.endingInventory) : null,
          currentStock: periodContainsToday ? currentStock : null,
          inventoryValue: periodContainsToday ? inventoryValue : null,
          soldBreakdown,
          endingBreakdown,
          confirmedPOBreakdown: periodAggregate?.confirmedPOs ?? null,
          draftPOBreakdown: periodAggregate?.draftPOs ?? null,
          stowedPOBreakdown: periodAggregate?.stowedPOs ?? null,
        });
      }

      result.set(inventoryGrouping.key, valuesForGrouping);
    }

    return result;
  }, [pagedInventoryGroupings, forecastsByKey, periodColumns, salesModifiers, manualOverrides]);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 3, width: "100%", minWidth: 0 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Inventory
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab value="forecasting" label="Forecasting" />
          <Tab value="summary" label="Summary" />
        </Tabs>

        {activeTab === "summary" && (
          <InventoryDashboard
            inventoryGroupings={filteredInventoryGroupingsWithMembers}
            forecastsPayload={forecastsPayload}
            consolidatedPOs={consolidatedPOs}
            isLoading={isLoading}
          />
        )}

        {activeTab === "forecasting" && (
        <>
        {/* Control Panel */}
        <ControlPanel
          bucketGranularity={bucketGranularity}
          onBucketChange={setBucketGranularity}
          groupingsPerPage={groupingsPerPage}
          onGroupingsPerPageChange={setGroupingsPerPage}
          inventoryFilterText={stagedInventoryFilterText}
          onInventoryFilterChange={setStagedInventoryFilterText}
          selectedCountries={stagedSelectedCountries}
          onCountriesChange={(value) => {
            setStagedSelectedCountries(value);
            setStagedHasUserInteractedWithCountryFilter(true);
          }}
          allCountries={allCountries}
          selectedManufacturers={stagedSelectedManufacturers}
          onManufacturersChange={(value) => {
            setStagedSelectedManufacturers(value);
            setStagedHasUserInteractedWithManufacturerFilter(true);
          }}
          allManufacturers={allManufacturers}
          selectedBrands={stagedSelectedBrands}
          onBrandsChange={(value) => {
            setStagedSelectedBrands(value);
            setStagedHasUserInteractedWithBrandFilter(true);
          }}
          allBrands={allBrands}
          startDate={stagedStartDate}
          onStartDateChange={setStagedStartDate}
          endDate={stagedEndDate}
          onEndDateChange={setStagedEndDate}
          runRateBasisMode={stagedRunRateBasisMode}
          onRunRateBasisModeChange={setStagedRunRateBasisMode}
          runRateBasisInput={stagedRunRateBasisInput}
          onRunRateBasisInputChange={setStagedRunRateBasisInput}
          fixedRunRateStart={stagedFixedRunRateStart}
          onFixedRunRateStartChange={setStagedFixedRunRateStart}
          fixedRunRateEnd={stagedFixedRunRateEnd}
          onFixedRunRateEndChange={setStagedFixedRunRateEnd}
          isSettingsDirty={isSettingsDirty}
          consolidatedPOsCount={consolidatedPOs.length}
          onOpenPODialog={() => po.openDialog()}
          onOpenLeadTimesDialog={() => setIsLeadTimesDialogOpen(true)}
          onSaveSettings={() => setIsConfirmingSaveSettings(true)}
          isSavingSettings={isSavingModifiers}
          onRefresh={handleRefresh}
          isRefreshing={isLoading}
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          totalItems={filteredInventoryGroupings.length}
          onPageChange={setCurrentPageIndex}
        />

        {/* Risk Summary Section */}
        <RiskSummarySection
          riskItems={riskSummaryItems}
          isLoading={isLoading}
          timeframeMonths={riskTimeframe}
          onTimeframeChange={setRiskTimeframe}
          bufferMonths={bufferMonths}
          onBufferMonthsChange={setBufferMonths}
          onViewInTable={scrollToTableRow}
          onLeadTimeChange={updateLeadTime}
          ignoredKeys={ignoredAlertKeys}
          ignoringKey={ignoringKey}
          onToggleIgnore={handleToggleIgnore}
        />

        {/* Inventory Projections Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#F8FAFC",
            borderRadius: "4px 4px 0 0",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Inventory Projections
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Consolidated pack rows</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={familyView}
              onChange={(_, next: FamilyView | null) => { if (next) changeFamilyView(next); }}
            >
              <ToggleButton value={FAMILY_VIEW.SHOW_CONSOLIDATED} sx={{ textTransform: "none", py: 0.25 }}>
                <Tooltip title="Show each family's consolidated 'all packs' row above its SKUs" arrow><span>Show</span></Tooltip>
              </ToggleButton>
              <ToggleButton value={FAMILY_VIEW.HIDE_CONSOLIDATED} sx={{ textTransform: "none", py: 0.25 }}>
                <Tooltip title="Hide the consolidated 'all packs' rows. Every SKU still shows as its own row." arrow><span>Hide</span></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Shrink every row in the table (all pages) to one line: just the ending inventory for each period" arrow>
              <Button size="small" sx={{ textTransform: "none", minWidth: 0, ml: 1 }} onClick={collapseAllRows}>Collapse all</Button>
            </Tooltip>
            <Tooltip title="Show every row's full numbers again" arrow>
              <span>
                <Button size="small" sx={{ textTransform: "none", minWidth: 0 }} disabled={collapsedRowKeys.size === 0} onClick={expandAllRows}>Expand all</Button>
              </span>
            </Tooltip>
            <Typography variant="caption" sx={{ opacity: 0.7, ml: 1 }}>Sort by</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={productArrangement.sort}
              onChange={(_, nextSort: ProductSort | null) => { if (nextSort) handleProductSortChange(nextSort); }}
            >
              <ToggleButton value={PRODUCT_SORT.SALES_VOLUME} sx={{ textTransform: "none", py: 0.25 }}>Sales volume</ToggleButton>
              <ToggleButton value={PRODUCT_SORT.PRODUCT_NUMBER} sx={{ textTransform: "none", py: 0.25 }}>
                <Tooltip title="Groups each product's sizes smallest to largest, the order a PO is written in. Products with no Danican # go last." arrow>
                  <span>Danican product #</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value={PRODUCT_SORT.CUSTOM} sx={{ textTransform: "none", py: 0.25 }}>
                <Tooltip title="The order you made with the arrows or by dragging. Moving a product switches to it. Saved for you in this browser." arrow>
                  <span>My order</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Undo your last change to this view — sort, pin or reorder (Ctrl/⌘ + Z)" arrow>
              <span>
                <Button size="small" sx={{ textTransform: "none", minWidth: 0 }} disabled={viewUndoStack.length === 0} onClick={undoViewChange}>
                  Undo
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Back to the standard view: sorted by sales volume, nothing pinned, your order cleared. Undo brings it back." arrow>
              <span>
                <Button
                  size="small"
                  sx={{ textTransform: "none", minWidth: 0 }}
                  disabled={isDefaultArrangement(productArrangement)}
                  onClick={() => { updateProductArrangement(DEFAULT_ARRANGEMENT); setCurrentPageIndex(0); }}
                >
                  Reset view
                </Button>
              </span>
            </Tooltip>
            {productArrangement.pinnedKeys.length > 0 && (
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => updateProductArrangement({ ...productArrangement, pinnedKeys: [] })}>
                Unpin all ({productArrangement.pinnedKeys.length})
              </Button>
            )}
          </Box>
        </Box>

        {/* Inventory Table */}
        <InventoryTable
          pagedInventoryGroupings={pagedInventoryGroupings}
          periodColumns={periodColumns}
          metricValuesByGroupingKey={metricValuesByGroupingKey}
          salesModifiers={salesModifiers}
          bucketGranularity={bucketGranularity}
          isLoading={isLoading}
          isLoadingHistory={isLoadingForecasts}
          errorMessage={errorMessage}
          onUpdateGlobalModifier={updateGlobalModifier}
          onUpdateInventoryModifier={updateInventoryModifier}
          onClearInventoryModifiers={clearInventoryModifiers}
          editingInventoryModifiers={editingInventoryModifiers}
          onSetEditingInventoryModifiers={setEditingInventoryModifiers}
          inventoryRunRateModifiers={inventoryRunRateModifiers}
          getFuturePOs={getFuturePOs}
          getConfirmedPOs={getConfirmedPOs}
          getDraftPOs={getDraftPOs}
          onOpenIncomingPODialog={openIncomingPODialog}
          onMarkReceived={handleMarkReceived}
          onPromotePO={handlePromotePO}
          onOpenSkuModal={(skus, inventoryName) => setSkuModalData({ skus, inventoryName })}
          onOpenProjectionDetail={openProjectionDetailDialog}
          onRegisterRowRef={(groupingKey, element) => {
            if (element) {
              tableRowRefs.current.set(groupingKey, element);
            } else {
              tableRowRefs.current.delete(groupingKey);
            }
          }}
          productRowControls={productRowControls}
          whatIfControls={whatIfControls}
          familyControls={familyControls}
        />

        <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.75 }}>
          Forecasts are computed server-side and refreshed when settings change.
        </Typography>
        </>
        )}

        {/* Dialogs */}
        <SavePricesToSkuMasterDialog
          open={priceChangesToOffer != null}
          onClose={() => setPriceChangesToOffer(null)}
          poNumber={priceChangesToOffer?.poNumber ?? ""}
          changes={priceChangesToOffer?.changes ?? []}
          productNumberChanges={priceChangesToOffer?.productNumberChanges ?? []}
          onSaved={() => inventoryService.getCatalogPrices().then(result => { if (result) setCatalogSuggestions(result); })}
        />

        <POManagementDialog
          {...po.dialogProps}
          consolidatedPOs={consolidatedPOs}
          catalogSuggestions={catalogSuggestions}
          onSuggestPONumber={handleSuggestPoNumber}
          onGeneratePdf={handleGeneratePdf}
          generatingPdfPoNumber={generatingPdfPoNumber}
          onPromotePO={(poToPromote) => handlePromotePO(poToPromote.poNumber)}
          onMarkReceived={(poToMark) => handleMarkReceived([poToMark.poNumber])}
          onUnmarkReceived={(poToUnmark) => handleUnmarkReceived([poToUnmark.poNumber])}
          onBulkMarkReceived={(poNumbers) => handleMarkReceived(poNumbers)}
          onBulkUnmarkReceived={(poNumbers) => handleUnmarkReceived(poNumbers)}
          wroOptions={wros}
          onLinkWRO={handleLinkWRO}
          onSetWroAllocations={handleSetWroAllocations}
          onSetWroLineAllocations={handleSetWroLineAllocations}
          onSuppliersChanged={setSuppliers}
          onWroCreated={() => setRefreshTrigger(prev => prev + 1)}
          onNetSuitePushed={() => setRefreshTrigger(prev => prev + 1)}
        />

        <SkuListModal
          skuModalData={skuModalData}
          onClose={() => setSkuModalData(null)}
        />

        <LeadTimesDialog
          open={isLeadTimesDialogOpen}
          onClose={() => setIsLeadTimesDialogOpen(false)}
          leadTimes={leadTimes}
          manufacturers={allManufacturers}
          countries={allCountries}
          inventoryOptions={inventoryOptions}
          onSaveManufacturer={updateManufacturerLeadTime}
          onSaveInventoryOverride={updateLeadTime}
          onSaveCountryAdjustment={updateCountryAdjustment}
          onDelete={removeLeadTime}
        />

        <DeletePOConfirmation
          poToDelete={po.poToDelete}
          isDeletingPO={po.isDeletingPO}
          onConfirm={po.confirmDeletePO}
          onCancel={po.cancelDeletePO}
        />

        <Dialog open={isConfirmingSaveSettings} onClose={() => setIsConfirmingSaveSettings(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Save these settings for everyone?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This saves the global sales multipliers and every product adjustment on this page. Everyone who opens
              the Inventory tool will see them, and they drive the team's real forecast — out-of-stock dates,
              recommended order dates and the out-of-stock alerts — until someone changes them. The page then reloads.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsConfirmingSaveSettings(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => { setIsConfirmingSaveSettings(false); saveSettingsToApi(); }}>
              Save for everyone
            </Button>
          </DialogActions>
        </Dialog>

        <SaveWhatIfDialog
          productName={savingWhatIfInventoryId
            ? allInventoryGroupings.find(grouping => grouping.inventory_id === savingWhatIfInventoryId)?.inventory_name || savingWhatIfInventoryId
            : null}
          settings={productDemandSettings(currentDemandSettings, savingWhatIfInventoryId ?? "")}
          onConfirm={() => saveWhatIf(savingWhatIfInventoryId!)}
          onClose={() => setSavingWhatIfInventoryId(null)}
        />

        <SalesProjectionDetailDialog
          inventoryGrouping={projectionDetailInventoryGrouping}
          runRateBasisMode={runRateBasisMode}
          runRateBasis={runRateBasis}
          fixedRunRateStart={fixedRunRateStart}
          fixedRunRateEnd={fixedRunRateEnd}
          salesModifiers={salesModifiers}
          manualOverrides={manualOverrides}
          serverForecast={projectionDetailInventoryGrouping
            ? forecastsByKey.get(projectionDetailInventoryGrouping.key)
            : null}
          onUpdateManualOverride={updateManualOverride}
          onUpdateInventoryModifier={updateInventoryModifier}
          onClose={closeProjectionDetailDialog}
          isLoadingSalesSeries={projectionSeriesStatus?.key === projectionDetailInventoryGrouping?.key && projectionSeriesStatus?.loading === true}
          salesSeriesError={projectionSeriesStatus?.key === projectionDetailInventoryGrouping?.key ? projectionSeriesStatus?.error ?? null : null}
          onSave={() => projectionDetailInventoryGrouping && setSavingWhatIfInventoryId(projectionDetailInventoryGrouping.inventory_id)}
          whatIf={projectionDetailInventoryGrouping ? demandWhatIf.simulationsByKey[projectionDetailInventoryGrouping.key] : undefined}
          hasUnsavedAdjustments={projectionDetailInventoryGrouping != null && demandWhatIf.unsavedIds.has(projectionDetailInventoryGrouping.inventory_id)}
          trialUnitsPerDay={projectionDetailInventoryGrouping ? demandWhatIf.trialRatesByKey[projectionDetailInventoryGrouping.key] : undefined}
          onTrialUnitsPerDayChange={(unitsPerDay) => projectionDetailInventoryGrouping && demandWhatIf.setTrialRate(projectionDetailInventoryGrouping.key, unitsPerDay)}
          onDiscardWhatIf={() => projectionDetailInventoryGrouping && discardWhatIf(projectionDetailInventoryGrouping.inventory_id)}
        />
      </Box>
    </LocalizationProvider>
  );
}
